import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDashboardNotifications } from "../../context/DashboardNotificationsContext";
import {
  getCategoryGradient,
  getCategoryMeta,
  isCriticalPriority,
} from "../../data/DashboardNotifications/notificationStyles";

const AUTO_DISMISS_MS = 8000;
const MAX_VISIBLE_TOASTS = 3;

const Toast = ({ message, onDismiss, reduceMotion }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const meta = getCategoryMeta(message.category);
  const critical = isCriticalPriority(message.priority);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(100);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const remainingRef = useRef(AUTO_DISMISS_MS);
  const startedAtRef = useRef(null);

  useEffect(() => {
    if (paused) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        remainingRef.current -= Date.now() - startedAtRef.current;
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    startedAtRef.current = Date.now();
    timerRef.current = setTimeout(onDismiss, remainingRef.current);

    const totalMs = AUTO_DISMISS_MS;
    const startRemaining = remainingRef.current;
    const tick = () => {
      const elapsed = Date.now() - startedAtRef.current;
      const remaining = Math.max(startRemaining - elapsed, 0);
      setProgress((remaining / totalMs) * 100);
      if (remaining > 0) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      clearTimeout(timerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line
  }, [paused]);

  return (
    <Box
      role="status"
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      sx={{
        width: 330,
        maxWidth: "90vw",
        borderRadius: "12px",
        overflow: "hidden",
        bgcolor: isDark ? "#1a2b3c" : "#ffffff",
        boxShadow: critical
          ? `0 0 0 2px ${meta.accent}, 0 12px 32px ${meta.accent}55`
          : `0 12px 32px ${meta.accent}33, 0 2px 8px rgba(0,0,0,0.12)`,
        ...(reduceMotion
          ? {}
          : {
              animation: "dashboardToastSlideIn 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
              "@keyframes dashboardToastSlideIn": {
                from: { transform: "translateX(40px)", opacity: 0 },
                to: { transform: "translateX(0)", opacity: 1 },
              },
            }),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, p: 1.5 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            background: getCategoryGradient(meta, isDark),
            boxShadow: `0 2px 8px ${meta.accent}55`,
          }}
        >
          <meta.Icon sx={{ color: "#fff", fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: isDark ? "#f1f5f9" : "#1e293b" }}
          >
            {message.title}
          </Typography>
          <Typography
            variant="body2"
            noWrap
            sx={{ color: isDark ? "rgba(255,255,255,0.7)" : "#475569" }}
          >
            {message.body}
          </Typography>
          {message.ctaLabel && message.ctaUrl && (
            <Link
              href={message.ctaUrl}
              variant="body2"
              sx={{ fontWeight: 700, color: meta.accent }}
            >
              {message.ctaLabel}
            </Link>
          )}
        </Box>
        <IconButton
          size="small"
          aria-label={`Dismiss notification: ${message.title}`}
          onClick={onDismiss}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ height: 3, bgcolor: `${meta.accent}22` }}>
        <Box
          sx={{
            height: "100%",
            width: `${progress}%`,
            background: getCategoryGradient(meta, isDark),
            transition: paused ? "none" : "width 0.1s linear",
          }}
        />
      </Box>
    </Box>
  );
};

const NotificationToastContainer = () => {
  const { toasts, dismiss } = useDashboardNotifications();
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const visibleToasts = toasts.slice(0, MAX_VISIBLE_TOASTS);

  if (visibleToasts.length === 0) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 1400,
        display: "flex",
        flexDirection: "column-reverse",
        gap: 1,
      }}
    >
      {visibleToasts.map((message) => (
        <Toast
          key={message.id}
          message={message}
          onDismiss={() => dismiss(message.id)}
          reduceMotion={reduceMotion}
        />
      ))}
    </Box>
  );
};

export default NotificationToastContainer;
